import {
  isSupplierShortlisted,
  normalizeSupplier,
  SupplierView,
  suppliersOf,
} from '../components/discovery/discoveryModel';
import { discoveryLayoutOf, listingKeyOf, pickedSupplierCount } from '../components/discovery/listingGroups';

/**
 * Pure mapping between a BOM line's listings and the shortlist the buyer
 * ticks: one tick per product row (listing key), saved as listing ids, with
 * supplier names alongside for the name-keyed callers. Only listings in the
 * main list — matching the line's spec — are ever ticked.
 */

export const listingsOf = (component: unknown): SupplierView[] => suppliersOf(component).map(normalizeSupplier);

const mainListingsOf = (component: unknown): SupplierView[] =>
  discoveryLayoutOf(listingsOf(component)).main.flatMap((g) => g.listings);

/** The listing keys the server holds as shortlisted, main list only. */
export const serverTickedKeys = (component: unknown): string[] => {
  const shortlisted = new Set(
    suppliersOf(component)
      .filter(isSupplierShortlisted)
      .map((s: { listingId?: string; supplierName?: string; name?: string }) => s?.listingId ?? s?.supplierName ?? s?.name),
  );
  return mainListingsOf(component).map(listingKeyOf).filter((key) => shortlisted.has(key));
};

/** Robbie's picks for the line: each pick supplier's best listing. */
export const pickKeysOf = (component: unknown): string[] =>
  discoveryLayoutOf(listingsOf(component)).picks.map((g) => listingKeyOf(g.best));

/**
 * Supplier names (Robbie's shortlist card) → listing keys: a supplier keeps
 * whichever of its products are already ticked, else gets its best product.
 */
export const keysForNames = (component: unknown, names: readonly string[], current: readonly string[]): string[] => {
  const groups = discoveryLayoutOf(listingsOf(component)).main;
  return names.flatMap((name) => {
    const group = groups.find((g) => g.listings.some((s) => s.name === name));
    if (!group) return [];
    const kept = group.listings.map(listingKeyOf).filter((key) => current.includes(key));
    return kept.length > 0 ? kept : [listingKeyOf(group.best)];
  });
};

/**
 * The line's entry in the shortlist save. Listing ids go only when every
 * listing carries one (a payload from before they existed sends names alone).
 */
export const shortlistItemFor = (component: unknown, componentId: string, keys: readonly string[]) => {
  const listings = listingsOf(component);
  const ticked = listings.filter((s) => keys.includes(listingKeyOf(s)));
  const suppliers = Array.from(new Set(ticked.map((s) => s.name)));
  const withIds = listings.length > 0 && listings.every((s) => s.listingId);
  return withIds ? { componentId, suppliers, listings: ticked.map((s) => s.listingId as string) } : { componentId, suppliers };
};

/** Suppliers ticked on the line — two products of one supplier count once. */
export const tickedSupplierCount = (component: unknown, keys: readonly string[]): number =>
  pickedSupplierCount(listingsOf(component), keys);
